/**
 * Settings.tsx
 * 设置页面
 *
 * 功能:
 * - 个人信息管理
 * - 密码修改
 * - 设备管理
 * - 登录安全
 * - 存储空间
 * - WebDAV 配置
 */

import { useI18n, translate } from '../i18n';
import { useState } from 'react';
import { useAuthStore } from '@/stores/auth';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { authApi, type EmailPreferences } from '@/services/api';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/Card';
import { StorageBar } from '@/components/files/StorageBar';
import { useToast } from '@/components/ui/useToast';
import { formatBytes, formatDate } from '@/utils';
import { cn } from '@/utils';
import {
  User,
  Lock,
  Trash2,
  Server,
  Eye,
  EyeOff,
  AlertTriangle,
  CheckCircle2,
  Copy,
  Globe,
  Shield,
  Monitor,
  Smartphone,
  Tablet,
  Laptop,
  Trash2 as TrashIcon,
  Clock,
  MapPin,
  Loader2,
  Sparkles,
  Mail,
  KeyRound,
  RefreshCw,
} from 'lucide-react';
import { AISettings } from '@/components/ai';
import { WebDavCredentials } from '@/components/settings/WebDavCredentials';
import LanguageSelect from '../components/ui/LanguageSelect';

type SettingsTab = 'profile' | 'email' | 'security' | 'ai';

function getDeviceIcon(userAgent: string): typeof Monitor {
  const ua = userAgent.toLowerCase();
  if (/mobile|android|iphone|ipod|blackberry|iemobile|opera mini/i.test(ua)) {
    if (/tablet|ipad/i.test(ua)) return Tablet;
    return Smartphone;
  }
  if (/tablet|ipad/i.test(ua)) return Tablet;
  if (/laptop|notebook/i.test(ua)) return Laptop;
  return Monitor;
}

function getBrowserName(userAgent: string): string {
  const ua = userAgent.toLowerCase();
  if (ua.includes('edg/')) return 'Edge';
  if (ua.includes('chrome/')) return 'Chrome';
  if (ua.includes('firefox/')) return 'Firefox';
  if (ua.includes('safari/') && !ua.includes('chrome')) return 'Safari';
  if (ua.includes('opera') || ua.includes('opr/')) return 'Opera';
  return translate("浏览器");
}

function getOSName(userAgent: string): string {
  const ua = userAgent.toLowerCase();
  if (ua.includes('windows')) return 'Windows';
  if (ua.includes('mac os')) return 'macOS';
  if (ua.includes('linux')) return 'Linux';
  if (ua.includes('android')) return 'Android';
  if (ua.includes('iphone') || ua.includes('ipad')) return 'iOS';
  return translate("未知系统");
}

function EmailChangeForm() {
  const { t } = useI18n();
  const { toast } = useToast();
  const [newEmail, setNewEmail] = useState('');
  const [password, setPassword] = useState('');

  const changeEmailMutation = useMutation({
    mutationFn: () => authApi.changeEmail({ newEmail, password }),
    onSuccess: () => {
      toast({ title: t("确认邮件已发送"), description: t("请查收新邮箱并点击确认链接") });
      setNewEmail('');
      setPassword('');
    },
    onError: (e: any) => {
      toast({
        title: t("发送失败"),
        description: e.response?.data?.error?.message || t("请检查输入"),
        variant: 'destructive',
      });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newEmail || !password) {
      toast({ title: t("请填写完整信息"), variant: 'destructive' });
      return;
    }
    changeEmailMutation.mutate();
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      <Input type="email" placeholder={t("新邮箱地址")} value={newEmail} onChange={(e) => setNewEmail(e.target.value)} />
      <Input type="password" placeholder={t("当前密码")} value={password} onChange={(e) => setPassword(e.target.value)} />
      <Button type="submit" size="sm" disabled={changeEmailMutation.isPending}>
        {changeEmailMutation.isPending ? t("发送中...") : t("发送确认邮件")}
      </Button>
    </form>
  );
}

function EmailPreferencesForm() {
  const { t } = useI18n();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: preferences, isLoading } = useQuery({
    queryKey: ['email-preferences'],
    queryFn: () => authApi.getEmailPreferences().then((r) => r.data.data),
  });

  const updateMutation = useMutation({
    mutationFn: (data: Partial<EmailPreferences>) => authApi.setEmailPreferences(data),
    onSuccess: () => {
      toast({ title: t("偏好已保存") });
      queryClient.invalidateQueries({ queryKey: ['email-preferences'] });
    },
    onError: (e: any) => {
      toast({
        title: t("保存失败"),
        description: e.response?.data?.error?.message,
        variant: 'destructive',
      });
    },
  });

  const isEnabled = (key: keyof EmailPreferences) =>
    preferences?.[key] ?? (key === 'login_success' || key === 'login_failed');

  const handleToggle = (key: keyof EmailPreferences) => {
    if (!preferences) return;
    updateMutation.mutate({ [key]: !isEnabled(key) });
  };

  if (isLoading) {
    return <div className="text-sm text-muted-foreground">{t("加载中...")}</div>;
  }

  const preferenceItems = [
    { key: 'login_success' as const, label: t("登录成功通知"), desc: t("登录成功时发送邮件，默认开启") },
    {
      key: 'login_failed' as const,
      label: t("登录失败通知"),
      desc: t("密码或两步验证失败、账户锁定时通知，同一账户5分钟内最多一封，默认开启"),
    },
    { key: 'mention' as const, label: t("@提及通知"), desc: t("当有人在文件或评论中@您时发送邮件") },
    { key: 'share_received' as const, label: t("分享接收通知"), desc: t("当他人分享文件给您时发送邮件") },
    { key: 'quota_warning' as const, label: t("配额警告"), desc: t("存储空间即将用尽时发送警告邮件") },
    { key: 'ai_complete' as const, label: t("AI处理完成"), desc: t("AI摘要或标签处理完成时发送邮件") },
    { key: 'system' as const, label: t("系统通知"), desc: t("重要的系统更新和安全提醒") },
  ];

  return (
    <div className="space-y-3">
      {preferenceItems.map((item) => (
        <div key={item.key} className="flex items-start justify-between gap-4 p-3 rounded-lg border">
          <div className="flex-1">
            <p className="text-sm font-medium">{item.label}</p>
            <p className="text-xs text-muted-foreground mt-0.5">{item.desc}</p>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={isEnabled(item.key)}
            aria-label={item.label}
            onClick={() => handleToggle(item.key)}
            disabled={updateMutation.isPending}
            className={cn(
              'relative inline-flex h-6 w-11 items-center rounded-full transition-colors',
              isEnabled(item.key) ? 'bg-primary' : 'bg-muted'
            )}
          >
            <span
              className={cn(
                'inline-block h-4 w-4 transform rounded-full bg-white transition-transform',
                isEnabled(item.key) ? 'translate-x-6' : 'translate-x-1'
              )}
            />
          </button>
        </div>
      ))}
    </div>
  );
}

function TwoFactorAuthCard() {
  const { t } = useI18n();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [setupSecret, setSetupSecret] = useState('');
  const [setupToken, setSetupToken] = useState('');
  const [reauthValue, setReauthValue] = useState('');
  const [setupUri, setSetupUri] = useState('');
  const [code, setCode] = useState('');
  const [regenCode, setRegenCode] = useState('');
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const copyValue = (value: string, title: string) => {
    navigator.clipboard.writeText(value).then(() => toast({ title }));
  };

  const { data: status, isLoading } = useQuery({
    queryKey: ['totp-status'],
    queryFn: () => authApi.getTotpStatus().then((r) => r.data.data),
  });

  const { data: trustedDevices = [], isLoading: trustedLoading } = useQuery({
    queryKey: ['totp-trusted-devices'],
    queryFn: () => authApi.trustedTotpDevices().then((r) => r.data.data ?? []),
    enabled: !!status?.enabled,
  });

  const setupMutation = useMutation({
    mutationFn: async () => {
      const response = await authApi.reauth({ operation: 'totp:setup', ...(status?.enabled ? { code: reauthValue } : { password: reauthValue }) });
      return authApi.setupTotp(response.data.data!.challengeToken);
    },
    onSuccess: (res) => {
      setSetupSecret(res.data.data?.secret || '');
      setSetupUri(res.data.data?.otpauthUri || '');
      setSetupToken(res.data.data?.setupToken || '');
      setReauthValue('');
      setBackupCodes([]);
    },
    onError: (e: any) =>
      toast({ title: t("无法开始设置"), description: e.response?.data?.error?.message, variant: 'destructive' }),
  });

  const enableMutation = useMutation({
    mutationFn: () => authApi.enableTotp(code, setupToken),
    onSuccess: (res) => {
      setBackupCodes(res.data.data?.backupCodes || []);
      setSetupSecret('');
      setSetupToken('');
      setSetupUri('');
      setCode('');
      queryClient.invalidateQueries({ queryKey: ['totp-status'] });
      toast({ title: t("两步验证已开启") });
    },
    onError: (e: any) =>
      toast({ title: t("开启失败"), description: e.response?.data?.error?.message, variant: 'destructive' }),
  });

  const disableMutation = useMutation({
    mutationFn: () => authApi.disableTotp(code),
    onSuccess: () => {
      setCode('');
      setBackupCodes([]);
      queryClient.invalidateQueries({ queryKey: ['totp-status'] });
      toast({ title: t("两步验证已关闭") });
    },
    onError: (e: any) =>
      toast({ title: t("关闭失败"), description: e.response?.data?.error?.message, variant: 'destructive' }),
  });

  const regenerateMutation = useMutation({
    mutationFn: () => authApi.regenerateBackupCodes(regenCode),
    onSuccess: (res) => {
      setBackupCodes(res.data.data?.backupCodes || []);
      setRegenCode('');
      toast({ title: t("备用恢复码已重新生成") });
    },
    onError: (e: any) =>
      toast({ title: t("生成失败"), description: e.response?.data?.error?.message, variant: 'destructive' }),
  });

  const deleteTrustedMutation = useMutation({
    mutationFn: (id: string) => authApi.deleteTrustedTotpDevice(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['totp-trusted-devices'] });
      toast({ title: t("信任设备已撤销") });
    },
    onError: (e: any) =>
      toast({ title: t("撤销失败"), description: e.response?.data?.error?.message, variant: 'destructive' }),
  });

  const enabled = !!status?.enabled;

  return (
    <Card>
      <CardHeader className="pb-4">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-emerald-500/10 flex items-center justify-center">
            <Shield className="h-4 w-4 text-emerald-600" />
          </div>
          <div>
            <CardTitle className="text-base">{t("两步验证")}</CardTitle>
            <CardDescription>{t("使用一次性验证码保护登录")}</CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {isLoading ? (
          <div className="text-sm text-muted-foreground">{t("加载中...")}</div>
        ) : (
          <div className="flex items-center justify-between rounded-lg border p-3">
            <div>
              <p className="text-sm font-medium">{enabled ? t("已开启") : t("未开启")}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{t("TOTP 验证码每 30 秒刷新")}</p>
            </div>
            <Button size="sm" onClick={() => setupMutation.mutate()} disabled={!reauthValue || setupMutation.isPending}>
              {setupMutation.isPending ? t("验证中...") : enabled ? t("更换验证器") : t("开始设置")}
            </Button>
          </div>
        )}

        <Input type="password" value={reauthValue} onChange={(e) => setReauthValue(e.target.value)}
          placeholder={enabled ? t("旧验证器验证码或恢复码（更换前验证）") : t("当前密码（首次启用前验证）")} aria-label={t("设置两步验证前再次验证身份")} />

        {setupSecret && (
          <div className="space-y-3 rounded-lg border p-3 bg-muted/30">
            <div className="space-y-1.5">
              <label className="text-sm font-medium">{t("密钥")}</label>
              <div className="flex gap-2">
                <Input value={setupSecret} readOnly className="font-mono text-xs" />
                <Button variant="outline" size="icon" onClick={() => copyValue(setupSecret, t("密钥已复制"))}>
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </div>
            {setupUri && (
              <Button variant="outline" size="sm" onClick={() => copyValue(setupUri, t("验证器 URI 已复制"))}>
                <Copy className="h-4 w-4 mr-1.5" />
                {t("复制验证器 URI")}</Button>
            )}
            <div className="flex gap-2">
              <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder={t("6 位验证码")} />
              <Button onClick={() => enableMutation.mutate()} disabled={code.length < 6 || enableMutation.isPending}>
                {enableMutation.isPending ? t("验证中...") : enabled ? t("确认更换") : t("开启")}
              </Button>
            </div>
          </div>
        )}

        {enabled && (
          <div className="space-y-4">
            <div className="rounded-lg border p-3 space-y-3">
              <div className="flex items-center gap-2">
                <KeyRound className="h-4 w-4 text-muted-foreground" />
                <p className="text-sm font-medium">{t("备用恢复码")}</p>
              </div>
              <div className="flex gap-2">
                <Input value={regenCode} onChange={(e) => setRegenCode(e.target.value)} placeholder={t("验证码或备用码")} />
                <Button
                  variant="outline"
                  onClick={() => regenerateMutation.mutate()}
                  disabled={!regenCode || regenerateMutation.isPending}
                >
                  <RefreshCw className="h-4 w-4 mr-1.5" />
                  {regenerateMutation.isPending ? t("生成中...") : t("重新生成")}
                </Button>
              </div>
            </div>

            <div className="rounded-lg border p-3 space-y-3">
              <div>
                <p className="text-sm font-medium">{t("信任设备")}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{t("信任凭据仅在关联登录会话有效期内生效，退出或撤销设备后立即失效")}</p>
              </div>
              {trustedLoading ? (
                <div className="text-sm text-muted-foreground">{t("加载中...")}</div>
              ) : trustedDevices.length === 0 ? (
                <div className="text-sm text-muted-foreground">{t("暂无信任设备")}</div>
              ) : (
                <div className="space-y-2">
                  {trustedDevices.map((device) => (
                    <div key={device.id} className="flex items-center justify-between gap-3 rounded-md bg-muted/40 px-3 py-2">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium">{device.name || t("未命名设备")}</p>
                        <p className="text-xs text-muted-foreground">
                          {t("最近使用 {{p0}} · 过期 {{p1}}", { p0: formatDate(device.lastUsed), p1: formatDate(device.expiresAt) })}</p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-500 hover:text-red-600 hover:bg-red-500/10"
                        onClick={() => deleteTrustedMutation.mutate(device.id)}
                        disabled={deleteTrustedMutation.isPending}
                      >
                        <TrashIcon className="h-4 w-4 mr-1" />
                        {t("撤销")}</Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex gap-2">
              <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder={t("验证码或备用码")} />
              <Button variant="outline" onClick={() => disableMutation.mutate()} disabled={!code || disableMutation.isPending}>
                {disableMutation.isPending ? t("关闭中...") : t("关闭")}
              </Button>
            </div>
          </div>
        )}

        {backupCodes.length > 0 && (
          <div className="rounded-lg border p-3 space-y-3">
            <div className="flex items-center justify-between gap-3">
              <p className="text-sm font-medium">{t("请保存这些恢复码")}</p>
              <Button variant="outline" size="sm" onClick={() => copyValue(backupCodes.join('\n'), t("恢复码已复制"))}>
                <Copy className="h-4 w-4 mr-1.5" />
                {t("复制全部")}</Button>
            </div>
            <div className="grid grid-cols-2 gap-2 font-mono text-xs">
              {backupCodes.map((backupCode) => (
                <code key={backupCode} className="rounded bg-muted px-2 py-1">
                  {backupCode}
                </code>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function Settings() {
  const { t } = useI18n();
  const { user, updateUser, logout } = useAuthStore();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [activeTab, setActiveTab] = useState<SettingsTab>('profile');
  const [name, setName] = useState(user?.name || '');
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [showCurrentPw, setShowCurrentPw] = useState(false);
  const [showNewPw, setShowNewPw] = useState(false);
  const [deletePw, setDeletePw] = useState('');
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  const apiBase = import.meta.env.VITE_API_URL || '';
  const webdavUrl = `${apiBase}/dav`;

  const {
    data: devices = [],
    isLoading: devicesLoading,
    refetch: refetchDevices,
  } = useQuery({
    queryKey: ['devices'],
    queryFn: () => authApi.devices().then((r) => r.data.data ?? []),
    select: (data) => data.slice(0, 10),
  });

  const updateNameMutation = useMutation({
    mutationFn: () => authApi.patchMe({ name: name.trim() || undefined }),
    onSuccess: (res) => {
      const updated = res.data.data;
      if (updated) updateUser(updated);
      queryClient.invalidateQueries({ queryKey: ['stats'] });
      toast({ title: t("昵称已更新") });
    },
    onError: (e: unknown) =>
      toast({
        title: t("更新失败"),
        description: (e as { response?: { data?: { error?: { message?: string } } } }).response?.data?.error?.message,
        variant: 'destructive',
      }),
  });

  const updatePasswordMutation = useMutation({
    mutationFn: () => authApi.patchMe({ currentPassword: currentPw, newPassword: newPw }),
    onSuccess: () => {
      setCurrentPw('');
      setNewPw('');
      setConfirmPw('');
      toast({ title: t("密码已更新，请重新登录"), variant: 'default' });
      setTimeout(() => logout(), 1500);
    },
    onError: (e: unknown) =>
      toast({
        title: t("修改密码失败"),
        description: (e as { response?: { data?: { error?: { message?: string } } } }).response?.data?.error?.message,
        variant: 'destructive',
      }),
  });

  const deleteAccountMutation = useMutation({
    mutationFn: () => authApi.deleteMe(deletePw),
    onSuccess: () => {
      toast({ title: t("账户已注销") });
      logout();
    },
    onError: (e: unknown) =>
      toast({
        title: t("注销失败"),
        description: (e as { response?: { data?: { error?: { message?: string } } } }).response?.data?.error?.message,
        variant: 'destructive',
      }),
  });

  const deleteDeviceMutation = useMutation({
    mutationFn: (deviceId: string) => authApi.deleteDevice(deviceId),
    onSuccess: () => {
      toast({ title: t("设备已注销") });
      refetchDevices();
    },
    onError: (e: unknown) =>
      toast({
        title: t("注销失败"),
        description: (e as { response?: { data?: { error?: { message?: string } } } }).response?.data?.error?.message,
        variant: 'destructive',
      }),
  });

  const pwStrength = (pw: string): { level: 0 | 1 | 2 | 3; label: string; color: string } => {
    if (!pw) return { level: 0, label: '', color: '' };
    let score = 0;
    if (pw.length >= 8) score++;
    if (/[A-Z]/.test(pw) && /[a-z]/.test(pw)) score++;
    if (/[0-9]/.test(pw)) score++;
    if (/[^A-Za-z0-9]/.test(pw)) score++;
    if (score <= 1) return { level: 1, label: t("弱"), color: 'bg-red-500' };
    if (score === 2) return { level: 2, label: t("中"), color: 'bg-amber-500' };
    return { level: 3, label: t("强"), color: 'bg-emerald-500' };
  };
  const strength = pwStrength(newPw);
  const pwMatch = newPw && confirmPw && newPw !== confirmPw;

  const copyToClipboard = (text: string, msg: string) => {
    navigator.clipboard.writeText(text).then(() => toast({ title: msg }));
  };

  const currentDeviceId = devices.find((device) => device.isCurrent)?.deviceId;

  const tabs: { id: SettingsTab; label: string; icon: typeof User }[] = [
    { id: 'profile', label: t("个人信息"), icon: User },
    { id: 'email', label: t("邮箱设置"), icon: Mail },
    { id: 'security', label: t("安全设置"), icon: Shield },
    { id: 'ai', label: t("AI 功能"), icon: Sparkles },
  ];

  return (
    <div className="space-y-6 max-w-2xl">
      <div>
        <h1 className="text-xl lg:text-2xl font-bold">{t("设置")}</h1>
        <p className="text-muted-foreground text-sm mt-0.5">{t("管理您的账户与偏好")}</p>
      </div>

      <Card>
        <CardContent className="pt-6 flex items-center justify-between gap-4 flex-wrap">
          <div>
            <p className="text-sm font-medium">{t('界面语言')}</p>
            <p className="text-xs text-muted-foreground mt-1">{t('默认跟随系统，切换后立即生效，不会清空正在填写的内容。')}</p>
          </div>
          <LanguageSelect />
        </CardContent>
      </Card>

      <div className="flex gap-1 p-1 bg-muted/50 rounded-lg overflow-x-auto no-scrollbar">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={cn(
                'flex items-center gap-1.5 px-3 lg:px-4 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap flex-shrink-0',
                activeTab === tab.id
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              )}
            >
              <Icon className="h-4 w-4" />
              <span>{tab.label}</span>
            </button>
          );
        })}
      </div>

      {activeTab === 'profile' && (
        <>
          <Card>
            <CardHeader className="pb-4">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                  <User className="h-4 w-4 text-primary" />
                </div>
                <div>
                  <CardTitle className="text-base">{t("个人信息")}</CardTitle>
                  <CardDescription>{t("更新您的显示名称")}</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-1.5">
                <label className="text-sm font-medium">{t("邮箱")}</label>
                <Input value={user?.email || ''} disabled className="bg-muted/50" />
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-medium">{t("昵称")}</label>
                <div className="flex gap-2">
                  <Input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder={t("输入昵称（留空则使用邮箱）")}
                    onKeyDown={(e) => e.key === 'Enter' && updateNameMutation.mutate()}
                  />
                  <Button
                    onClick={() => updateNameMutation.mutate()}
                    disabled={updateNameMutation.isPending || name === (user?.name || '')}
                    size="sm"
                    className="flex-shrink-0"
                  >
                    {updateNameMutation.isPending ? t("保存中…") : t("保存")}
                  </Button>
                </div>
              </div>
              <div className="text-xs text-muted-foreground">
                {t("注册于")}{' '}{user?.createdAt ? formatDate(user.createdAt) : '—'}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-4">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                  <Server className="h-4 w-4 text-emerald-500" />
                </div>
                <div>
                  <CardTitle className="text-base">{t("存储空间")}</CardTitle>
                  <CardDescription>{t("您当前的存储使用情况")}</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <StorageBar used={user?.storageUsed || 0} quota={user?.storageQuota || 10737418240} />
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="bg-muted/40 rounded-lg px-4 py-3">
                  <p className="text-muted-foreground text-xs mb-1">{t("已使用")}</p>
                  <p className="font-semibold">{formatBytes(user?.storageUsed || 0)}</p>
                </div>
                <div className="bg-muted/40 rounded-lg px-4 py-3">
                  <p className="text-muted-foreground text-xs mb-1">{t("总配额")}</p>
                  <p className="font-semibold">{formatBytes(user?.storageQuota || 0)}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-4">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-purple-500/10 flex items-center justify-center">
                  <Globe className="h-4 w-4 text-purple-500" />
                </div>
                <div>
                  <CardTitle className="text-base">{t("WebDAV 访问")}</CardTitle>
                  <CardDescription>{t("使用 WebDAV 客户端挂载文件系统")}</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-1.5">
                <label className="text-sm font-medium">{t("WebDAV 地址")}</label>
                <div className="flex gap-2">
                  <Input value={webdavUrl} readOnly className="font-mono text-sm bg-muted/50" />
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => copyToClipboard(webdavUrl, t("已复制 WebDAV 地址"))}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <div className="rounded-lg bg-muted/40 p-4 space-y-1.5 text-sm text-muted-foreground">
                <div className="flex items-center justify-between">
                  <span>{t("用户名")}</span>
                  <div className="flex items-center gap-1">
                    <code className="text-foreground text-xs bg-muted px-1.5 py-0.5 rounded">{user?.email}</code>
                    <button onClick={() => copyToClipboard(user?.email || '', t("已复制邮箱"))}>
                      <Copy className="h-3.5 w-3.5 hover:text-foreground transition-colors" />
                    </button>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span>{t("密码")}</span>
                  <span className="text-xs">{t("使用独立应用凭据；登录密码仅适用于未启用两步验证的账户")}</span>
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                {t("支持 macOS Finder、Windows 资源管理器、Cyberduck、Mountain Duck 等客户端")}</p>
              <WebDavCredentials />
            </CardContent>
          </Card>
        </>
      )}

      {activeTab === 'email' && (
        <>
          <Card>
            <CardHeader className="pb-4">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                  <Mail className="h-4 w-4 text-emerald-600" />
                </div>
                <div>
                  <CardTitle className="text-base">{t("邮箱设置")}</CardTitle>
                  <CardDescription>{t("管理您的邮箱地址和通知偏好")}</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">{t("当前邮箱")}</label>
                <div className="flex items-center gap-2">
                  <Input value={user?.email || ''} disabled className="flex-1" />
                  {user?.emailVerified ? (
                    <span className="text-xs text-emerald-600 flex items-center gap-1">
                      <CheckCircle2 className="h-3 w-3" /> {t("已验证")}</span>
                  ) : (
                    <span className="text-xs text-amber-600 flex items-center gap-1">
                      <AlertTriangle className="h-3 w-3" /> {t("未验证")}</span>
                  )}
                </div>
              </div>

              {!user?.emailVerified && (
                <div className="p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg">
                  <p className="text-sm text-amber-900">
                    {t("您的邮箱尚未验证，部分功能受限。")}<button
                      onClick={() => {
                        authApi
                          .resendVerification({ email: user?.email || '' })
                          .then(() => toast({ title: t("验证邮件已发送") }))
                          .catch((e) =>
                            toast({
                              title: t("发送失败"),
                              description: e.response?.data?.error?.message,
                              variant: 'destructive',
                            })
                          );
                      }}
                      className="text-primary hover:underline ml-1"
                    >
                      {t("重发验证邮件")}</button>
                  </p>
                </div>
              )}

              <div className="pt-4 border-t">
                <h4 className="text-sm font-medium mb-3">{t("更换邮箱")}</h4>
                <p className="text-xs text-muted-foreground mb-3">{t("更换邮箱后需要重新验证，新邮箱将作为登录账号")}</p>
                <EmailChangeForm />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-4">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-blue-500/10 flex items-center justify-center">
                  <Mail className="h-4 w-4 text-blue-600" />
                </div>
                <div>
                  <CardTitle className="text-base">{t("邮件通知偏好")}</CardTitle>
                  <CardDescription>{t("选择您希望接收的邮件通知类型")}</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <EmailPreferencesForm />
            </CardContent>
          </Card>
        </>
      )}

      {activeTab === 'security' && (
        <>
          <Card>
            <CardHeader className="pb-4">
              <CardTitle className="text-base">{t("临时安全限制")}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm text-muted-foreground" role="status">
              <p>{t("账户安全操作仅支持登录会话。创建/扩大 API Key 权限和更换两步验证需再次验证身份；可信设备使用独立凭据，不按 IP/设备信息跳过验证。启用两步验证后，WebDAV 使用独立应用凭据。")}</p>
              <p>{t("任意 URL 离线下载、Webhook 投递/测试和未经批准的自定义存储或机器人端点访问已暂停，等待安全目标策略就绪。")}</p>
              <p>{t("新上传支持基于服务端凭据的单次直传和分片上传；仅在存储不支持直传或 PUT 失败时回退代理完整上传。旧分片任务不能续传或复用，请在文件列表重新选择原文件完整上传。含账户令牌的文件链接不能外发。HTML/SVG 仅允许附件下载；Office 禁用嵌入 HTML，表格仅显示值。离线缓存仅保留公共静态资源。")}</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-4">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-blue-500/10 flex items-center justify-center">
                  <Lock className="h-4 w-4 text-blue-500" />
                </div>
                <div>
                  <CardTitle className="text-base">{t("修改密码")}</CardTitle>
                  <CardDescription>{t("定期更换密码以保证账户安全")}</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-1.5">
                <label className="text-sm font-medium">{t("当前密码")}</label>
                <div className="relative">
                  <Input
                    type={showCurrentPw ? 'text' : 'password'}
                    value={currentPw}
                    onChange={(e) => setCurrentPw(e.target.value)}
                    placeholder={t("输入当前密码")}
                    className="pr-10"
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    onClick={() => setShowCurrentPw((v) => !v)}
                  >
                    {showCurrentPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-sm font-medium">{t("新密码")}</label>
                <div className="relative">
                  <Input
                    type={showNewPw ? 'text' : 'password'}
                    value={newPw}
                    onChange={(e) => setNewPw(e.target.value)}
                    placeholder={t("至少 6 个字符")}
                    className="pr-10"
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    onClick={() => setShowNewPw((v) => !v)}
                  >
                    {showNewPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {newPw && (
                  <div className="space-y-1">
                    <div className="flex gap-1">
                      {[1, 2, 3].map((lvl) => (
                        <div
                          key={lvl}
                          className={cn(
                            'h-1 flex-1 rounded-full transition-colors',
                            strength.level >= lvl ? strength.color : 'bg-secondary'
                          )}
                        />
                      ))}
                    </div>
                    <p className={cn('text-xs', strength.color.replace('bg-', 'text-'))}>{t("密码强度：{{p0}}", { p0: strength.label })}</p>
                  </div>
                )}
              </div>

              <div className="space-y-1.5">
                <label className="text-sm font-medium">{t("确认新密码")}</label>
                <Input
                  type="password"
                  value={confirmPw}
                  onChange={(e) => setConfirmPw(e.target.value)}
                  placeholder={t("再次输入新密码")}
                  className={cn(pwMatch && 'border-red-500 focus-visible:ring-red-500')}
                />
                {pwMatch && <p className="text-xs text-red-500">{t("两次输入的密码不一致")}</p>}
                {newPw && confirmPw && !pwMatch && (
                  <p className="text-xs text-emerald-500 flex items-center gap-1">
                    <CheckCircle2 className="h-3 w-3" /> {t("密码一致")}</p>
                )}
              </div>

              <Button
                onClick={() => updatePasswordMutation.mutate()}
                disabled={
                  !currentPw ||
                  !newPw ||
                  !confirmPw ||
                  !!pwMatch ||
                  newPw.length < 6 ||
                  updatePasswordMutation.isPending
                }
              >
                {updatePasswordMutation.isPending ? t("更新中…") : t("更新密码")}
              </Button>
            </CardContent>
          </Card>

          <TwoFactorAuthCard />

          <Card>
            <CardHeader className="pb-4">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-amber-500/10 flex items-center justify-center">
                  <Shield className="h-4 w-4 text-amber-500" />
                </div>
                <div>
                  <CardTitle className="text-base">{t("登录设备")}</CardTitle>
                  <CardDescription>{t("管理已登录的设备")}</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {devicesLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : devices.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground text-sm">{t("暂无已登录设备")}</div>
              ) : (
                <div className="space-y-3">
                  {devices.map((device) => {
                    const DeviceIcon = getDeviceIcon(device.userAgent || '');
                    const browser = getBrowserName(device.userAgent || '');
                    const os = getOSName(device.userAgent || '');
                    const isCurrent = device.deviceId === currentDeviceId;

                    return (
                      <div
                        key={device.id}
                        className={cn(
                          'flex items-center gap-4 p-4 rounded-lg border transition-colors',
                          isCurrent ? 'bg-primary/5 border-primary/20' : 'bg-muted/30'
                        )}
                      >
                        <div
                          className={cn(
                            'w-10 h-10 rounded-lg flex items-center justify-center',
                            isCurrent ? 'bg-primary/10' : 'bg-muted'
                          )}
                        >
                          <DeviceIcon className={cn('h-5 w-5', isCurrent ? 'text-primary' : 'text-muted-foreground')} />
                        </div>

                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-sm">
                              {browser} · {os}
                            </span>
                            {isCurrent && (
                              <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-primary/10 text-primary">
                                {t("当前设备")}</span>
                            )}
                          </div>
                          <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1">
                            {device.ipAddress && (
                              <span className="flex items-center gap-1">
                                <MapPin className="h-3 w-3" />
                                {device.ipAddress}
                              </span>
                            )}
                            <span className="flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {formatDate(device.lastActive)}
                            </span>
                          </div>
                        </div>

                        {!isCurrent && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-500 hover:text-red-600 hover:bg-red-500/10"
                            onClick={() => {
                              if (confirm(t("确定要注销此设备吗？"))) {
                                deleteDeviceMutation.mutate(device.deviceId);
                              }
                            }}
                            disabled={deleteDeviceMutation.isPending}
                          >
                            <TrashIcon className="h-4 w-4 mr-1" />
                            {t("注销")}</Button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              <div className="flex items-center gap-2 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20 mt-4">
                <AlertTriangle className="h-4 w-4 text-amber-500 flex-shrink-0" />
                <p className="text-xs text-amber-600 dark:text-amber-400">
                  {t("如果发现陌生设备登录，请立即修改密码并注销该设备")}</p>
              </div>
            </CardContent>
          </Card>

          <Card className="border-red-500/30">
            <CardHeader className="pb-4">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-red-500/10 flex items-center justify-center">
                  <AlertTriangle className="h-4 w-4 text-red-500" />
                </div>
                <div>
                  <CardTitle className="text-base text-red-500">{t("危险区域")}</CardTitle>
                  <CardDescription>{t("不可撤销的操作，请谨慎")}</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {!deleteConfirmOpen ? (
                <div className="flex items-center justify-between p-4 border border-red-500/20 rounded-lg bg-red-500/5">
                  <div>
                    <p className="text-sm font-medium">{t("注销账户")}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{t("永久删除账户及所有文件，此操作不可撤销")}</p>
                  </div>
                  <Button variant="destructive" size="sm" onClick={() => setDeleteConfirmOpen(true)}>
                    <Trash2 className="h-4 w-4 mr-1.5" />
                    {t("注销账户")}</Button>
                </div>
              ) : (
                <div className="space-y-3 p-4 border border-red-500/30 rounded-lg bg-red-500/5">
                  <div className="flex items-start gap-2 text-sm text-red-600 dark:text-red-400">
                    <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                    <p>{t("此操作将永久删除您的账户、所有文件和分享链接，且不可恢复。请输入密码确认。")}</p>
                  </div>
                  <Input
                    type="password"
                    placeholder={t("输入密码确认注销")}
                    value={deletePw}
                    onChange={(e) => setDeletePw(e.target.value)}
                    className="border-red-500/50 focus-visible:ring-red-500"
                    autoFocus
                  />
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setDeleteConfirmOpen(false);
                        setDeletePw('');
                      }}
                    >
                      {t("取消")}</Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      disabled={!deletePw || deleteAccountMutation.isPending}
                      onClick={() => deleteAccountMutation.mutate()}
                    >
                      {deleteAccountMutation.isPending ? t("注销中…") : t("确认永久注销")}
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {activeTab === 'ai' && (
        <Card>
          <CardContent className="pt-6">
            <AISettings />
          </CardContent>
        </Card>
      )}
    </div>
  );
}
